<template>
  <div class="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
    <!-- Header -->
    <div class="text-center mb-8">
      <h1 class="text-3xl font-bold text-gray-900">Buy Sparks</h1>
      <p class="mt-2 text-gray-600">
        Power your AI learning tools with more sparks
      </p>

      <!-- Current Balance -->
      <div class="mt-4 inline-block">
        <div
          class="flex items-center space-x-3 rounded-2xl p-4 border transition-all duration-300 bg-yellow-50 border-yellow-200"
        >
          <!-- Loading State -->
          <div v-if="sparksStore.isLoading" class="flex items-center space-x-2">
            <div class="animate-pulse">
              <div class="w-8 h-8 bg-gray-300 rounded-full"></div>
            </div>
            <div class="animate-pulse">
              <div class="h-6 bg-gray-300 rounded w-24"></div>
            </div>
          </div>

          <!-- Balance Display -->
          <div v-else class="flex items-center space-x-3">
            <PhLightning class="w-8 h-8 text-spark" weight="fill" />
            <div>
              <span class="font-mono text-2xl font-medium text-gray-900">
                {{ sparksStore.currentBalance.toLocaleString() }}
              </span>
              <span class="text-lg ml-1 text-gray-600">
                Spark{{ sparksStore.currentBalance === 1 ? "" : "s" }}
              </span>
            </div>
            <button
              v-if="sparksStore.isInitialized"
              @click="handleRefreshBalance"
              class="transition-colors ml-2 text-gray-500 hover:text-gray-700"
              title="Refresh balance"
            >
              <PhArrowsClockwise class="w-5 h-5" />
            </button>
          </div>
        </div>
      </div>
    </div>

    <!-- Spark Packages -->
    <div class="mb-12">
      <h2 class="text-xl font-semibold mb-6 text-gray-900">
        One-Time Spark Packages
      </h2>

      <!-- Loading State -->
      <div
        v-if="isLoadingPackages"
        class="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-6"
      >
        <div v-for="i in 5" :key="i" class="card animate-pulse">
          <div class="text-center">
            <div class="w-12 h-12 bg-gray-300 rounded-full mx-auto mb-4"></div>
            <div class="h-8 bg-gray-300 rounded mb-2"></div>
            <div class="h-4 bg-gray-300 rounded mb-4"></div>
            <div class="h-10 bg-gray-300 rounded mb-4"></div>
            <div class="h-6 bg-gray-300 rounded mb-3"></div>
            <div class="space-y-1 mb-4">
              <div class="h-3 bg-gray-300 rounded"></div>
              <div class="h-3 bg-gray-300 rounded"></div>
              <div class="h-3 bg-gray-300 rounded"></div>
            </div>
            <div class="h-10 bg-gray-300 rounded"></div>
          </div>
        </div>
      </div>

      <!-- Error State -->
      <div v-else-if="packagesError" class="card border-neon-pink/50 bg-red-50">
        <div class="text-center text-red-600">
          <PhWarningCircle class="w-12 h-12 mx-auto mb-4" weight="fill" />
          <h3 class="text-lg font-medium mb-2">Failed to Load Packages</h3>
          <p class="text-sm mb-4">{{ packagesError }}</p>
          <button @click="fetchSparkPackages" class="btn-primary">
            Try Again
          </button>
        </div>
      </div>

      <!-- Packages Grid -->
      <div
        v-else
        class="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-3"
      >
        <div
          v-for="package_ in sparkPackages"
          :key="package_.id"
          class="relative rounded-2xl p-6 cursor-pointer transition-all duration-300 border bg-white"
          :class="[
            package_.popular
              ? 'border-neon-green/50 hover:border-neon-green hover:shadow-neon-green-sm'
              : 'border-tile-border hover:border-neon-blue hover:shadow-neon-blue-sm',
          ]"
          @click="handleStripeCheckout(package_)"
        >
          <!-- Popular Badge -->
          <div
            v-if="package_.popular"
            class="absolute -top-3 left-1/2 transform -translate-x-1/2"
          >
            <span
              class="bg-neon-green text-bg-dark text-xs font-medium px-3 py-1 rounded-full"
            >
              Most Popular
            </span>
          </div>

          <div class="text-center">
            <!-- Spark Count -->
            <PhLightning
              class="w-8 h-8 text-spark mx-auto mb-1"
              weight="fill"
            />
            <h3 class="text-xl font-bold font-mono text-gray-900">
              {{ getPackageTotalSparks(package_).toLocaleString() }}
            </h3>
            <p class="text-sm mb-3 text-gray-600">Total Sparks</p>

            <!-- Price -->
            <div class="mb-2">
              <span class="text-2xl font-bold text-gray-900">
                ${{ (package_.price / 100).toFixed(0) }}
              </span>
            </div>

            <!-- Value -->
            <div class="text-xs mb-2 text-gray-500">
              <span v-if="package_.bonus" class="text-neon-green font-semibold">
                +{{ package_.bonus }} bonus sparks included!
              </span>
              <span v-else class="text-gray-400">
                {{
                  Math.round(
                    getPackageTotalSparks(package_) / (package_.price / 100)
                  )
                }}
                sparks per dollar
              </span>
            </div>

            <!-- What you can create -->
            <div class="mb-6 mt-4 border-t pt-3 border-gray-200">
              <p class="text-xs mb-2 font-medium text-gray-500">
                Examples of what you can create:
              </p>
              <ul class="text-xs space-y-0.5 text-gray-600">
                <li
                  v-for="(example, idx) in getExamplesWithTooltips(package_)"
                  :key="idx"
                  class="flex items-center group relative"
                >
                  <span class="text-neon-green mr-1.5">*</span>
                  <span class="cursor-help flex items-center">
                    {{ example.text }}
                    <!-- Info Icon -->
                    <PhInfo
                      class="w-3 h-3 ml-1 transition-colors text-gray-400 group-hover:text-gray-600"
                    />
                  </span>

                  <!-- Tooltip -->
                  <div
                    class="absolute left-0 bottom-full mb-1 px-2 py-1 text-xs rounded shadow-lg opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none whitespace-nowrap z-10 bg-gray-800 text-white"
                  >
                    {{ example.tooltip }}
                  </div>
                </li>
              </ul>
            </div>

            <!-- Buy Button -->
            <button
              @click.stop="handleStripeCheckout(package_)"
              :disabled="processingPackageId === package_.id"
              class="w-full py-2 px-4 rounded-xl font-medium transition-all duration-300"
              :class="
                processingPackageId === package_.id
                  ? 'bg-neon-green text-bg-dark opacity-75'
                  : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
              "
            >
              <span v-if="processingPackageId === package_.id" class="flex items-center justify-center">
                <div class="w-4 h-4 border-2 border-t-transparent rounded-full animate-spin mr-2 border-current"></div>
                Redirecting...
              </span>
              <span v-else>Buy Now</span>
            </button>
          </div>
        </div>
      </div>
    </div>

    <!-- Subscription Upsell -->
    <div class="card mb-8 bg-neon-green/10">
      <div class="text-center">
        <h3 class="text-xl font-semibold mb-2 text-gray-900">
          Need Sparks Monthly?
        </h3>
        <p class="mb-4 text-gray-600">
          Save money with a monthly subscription and get sparks automatically
          delivered.
        </p>
        <button @click="handleSubscriptionClick" class="btn-primary px-3">
          View Subscription Plans
        </button>
      </div>
    </div>

    <!-- FAQ -->
    <div class="mt-12">
      <h2 class="text-xl font-semibold mb-6 text-gray-900">
        Frequently Asked Questions
      </h2>
      <div class="space-y-4">
        <div class="card">
          <h4 class="font-medium mb-2 text-gray-900">
            How long do sparks last?
          </h4>
          <p class="text-sm text-gray-600">
            One-time spark purchases never expire. Subscription sparks rollover
            up to your plan limit.
          </p>
        </div>

        <div class="card">
          <h4 class="font-medium mb-2 text-gray-900">
            Can I use sparks across all products?
          </h4>
          <p class="text-sm text-gray-600">
            Yes! Your spark balance is shared across all Happy Alien products.
          </p>
        </div>

        <div class="card">
          <h4 class="font-medium mb-2 text-gray-900">
            What if I'm not satisfied?
          </h4>
          <p class="text-sm text-gray-600">
            We offer a 30-day money-back guarantee for all spark purchases.
          </p>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, onMounted, watch } from "vue";
import { useRoute, useRouter } from "vue-router";
import { useSparksStore } from "../stores/sparks";
import { useNotificationStore } from "../stores/notifications";
import { useAuthStore } from "../stores/auth";
import {
  PhLightning,
  PhArrowsClockwise,
  PhWarningCircle,
  PhInfo,
} from "@phosphor-icons/vue";

const route = useRoute();
const router = useRouter();
const sparksStore = useSparksStore();
const notificationStore = useNotificationStore();
const authStore = useAuthStore();

// State
const processingPackageId = ref<string | null>(null);

interface SparkPackage {
  id: string;
  stripeProductId?: string;
  stripePriceId?: string;
  sparks: number;
  price: number; // in cents
  bonus?: number; // number of bonus sparks
  popular?: boolean;
  nickname?: string;
}

// Dynamic data from Stripe API
const sparkPackages = ref<SparkPackage[]>([]);
const isLoadingPackages = ref(true);
const packagesError = ref<string | null>(null);

// Product costs in Sparks
const SPARK_COSTS = {
  image: 4, // Gemini 2.5 Flash
  audioEffect: 6, // ElevenLabs sound effects
  ttsPerMin: 15, // Text-to-speech
  veo2Per8s: 400, // Video generation Veo 2
  vidsabioPerMin: 100, // VidSabio processing
  characterPack: 60, // Character pack generation
};

// Helper functions for spark usage examples
const nf = new Intl.NumberFormat(undefined, {
  notation: "compact",
  maximumFractionDigits: 1,
});

function getPackageTotalSparks(package_: SparkPackage): number {
  return package_.sparks + (package_.bonus || 0);
}

function formatMinutesToHours(minutes: number): string {
  if (minutes < 60) {
    return `${minutes} min`;
  }
  const hours = minutes / 60;
  if (hours < 10) {
    return `${hours.toFixed(1)} hours`;
  }
  return `${Math.round(hours)} hours`;
}

function getExamplesWithTooltips(package_: SparkPackage) {
  const total = getPackageTotalSparks(package_);

  // Calculate exact counts using floor division
  const images = Math.floor(total / SPARK_COSTS.image);
  const audioEffects = Math.floor(total / SPARK_COSTS.audioEffect);
  const ttsMinutes = Math.floor(total / SPARK_COSTS.ttsPerMin);

  // Format examples with tooltips
  const examples = [];

  // Always show images first
  examples.push({
    text: `${nf.format(images)} AI images, or`,
    tooltip: `Based on ${SPARK_COSTS.image} sparks per image`,
  });

  // Always show sound effects second
  examples.push({
    text: `${nf.format(audioEffects)} sound effects, or`,
    tooltip: `Based on ${SPARK_COSTS.audioEffect} sparks per sound effect`,
  });

  // Show voice/TTS third
  examples.push({
    text: `${formatMinutesToHours(ttsMinutes)} of voice`,
    tooltip: `Based on ${SPARK_COSTS.ttsPerMin} sparks per minute`,
  });

  return examples;
}

// Methods
function handlePaymentResult() {
  // Check for success/cancel parameters from Stripe redirect
  const success = route.query.success;
  const canceled = route.query.canceled;

  if (success === "true") {
    notificationStore.success(
      "Payment Successful!",
      "Your sparks have been added to your account."
    );
    // Refresh balance to show new sparks
    sparksStore.refreshBalance();
    // Clean up URL
    router.replace({ name: "BuyCredits" });
  } else if (canceled === "true") {
    notificationStore.info(
      "Payment Canceled",
      "Your payment was canceled. No charges were made."
    );
    // Clean up URL
    router.replace({ name: "BuyCredits" });
  }
}

async function fetchSparkPackages() {
  try {
    isLoadingPackages.value = true;
    packagesError.value = null;

    const response = await fetch(
      `${import.meta.env.VITE_API_URL}/products/stripe`,
      {
        headers: {
          Authorization: `Bearer ${authStore.accessToken}`,
          "Content-Type": "application/json",
        },
      }
    );

    if (!response.ok) {
      throw new Error("Failed to fetch spark packages");
    }

    const result = await response.json();
    if (result.success) {
      sparkPackages.value = result.data.packages;
    } else {
      throw new Error(result.error || "Failed to load packages");
    }
  } catch (error) {
    console.error("Error fetching spark packages:", error);
    packagesError.value =
      "Failed to load spark packages. Please refresh the page to try again.";
    notificationStore.error(
      "Loading Error",
      "Failed to load spark packages from Stripe. Please refresh the page."
    );
  } finally {
    isLoadingPackages.value = false;
  }
}

async function handleRefreshBalance() {
  try {
    await sparksStore.refreshBalance();
  } catch (error) {
    notificationStore.error(
      "Refresh Failed",
      "Unable to refresh balance. Please try again."
    );
  }
}

function handleSubscriptionClick() {
  router.push({ name: "Subscription" });
}

async function handleStripeCheckout(package_: SparkPackage) {
  if (processingPackageId.value) return;

  processingPackageId.value = package_.id;

  try {
    const response = await fetch(
      `${import.meta.env.VITE_API_URL}/products/stripe/checkout`,
      {
        method: "POST",
        headers: {
          Authorization: `Bearer ${authStore.accessToken}`,
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          priceId: package_.stripePriceId || package_.id,
        }),
      }
    );

    if (!response.ok) {
      throw new Error("Failed to create checkout session");
    }

    const result = await response.json();

    if (!result.success) {
      throw new Error(result.error || "Failed to create checkout session");
    }

    window.location.href = result.data.checkout_url;
  } catch (error) {
    console.error("Checkout error:", error);
    notificationStore.error(
      "Payment Failed",
      "There was an error creating your payment session. Please try again."
    );
    processingPackageId.value = null;
  }
}

// Lifecycle
onMounted(async () => {
  // Handle payment result from Stripe redirect
  handlePaymentResult();

  // Wait for SDK to be initialized before refreshing balance and fetching packages
  if (sparksStore.isInitialized) {
    await Promise.all([sparksStore.refreshBalance(), fetchSparkPackages()]);
  } else {
    // If SDK is not initialized yet, wait for it
    const unwatch = watch(
      () => sparksStore.isInitialized,
      async (isInitialized) => {
        if (isInitialized) {
          await Promise.all([
            sparksStore.refreshBalance(),
            fetchSparkPackages(),
          ]);
          unwatch(); // Stop watching once initialized
        }
      }
    );
  }
});
</script>
